import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import './NewsDetail.css';

function NewsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Retrieve article from sessionStorage
    const storedArticle = sessionStorage.getItem(`news_${id}`);
    
    if (storedArticle) {
      try {
        const parsedArticle = JSON.parse(storedArticle);
        setArticle(parsedArticle);
      } catch (err) {
        console.error('Error parsing article:', err);
        setError('Failed to load article details');
      }
    } else {
      setError('Article not found');
    }
    
    setLoading(false);
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="news-detail-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="news-detail-container">
        <div className="error-container">
          <h2>Article Not Found</h2>
          <p>{error || 'The requested article could not be found.'}</p>
          <button onClick={() => navigate('/')} className="back-button">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="news-detail-container">
      <SEO
        title={`${article.title} - Pharmaceutical Database News`}
        description={article.description || article.title}
        keywords={`${article.category || 'pharmaceutical'}, ${article.tags ? article.tags.join(', ') : 'health news, medical research'}, ${article.drugName || 'pharmaceutical news'}`}
        canonical={`/news/${id}`}
        type="article"
        image={article.urlToImage || '/logo512.png'}
        article={{
          publishedTime: article.publishedAt,
          modifiedTime: article.publishedAt,
          author: article.source || 'Pharmaceutical Database',
          section: article.category || 'Health',
          tags: article.tags || []
        }}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'NewsArticle',
          headline: article.title,
          description: article.description,
          image: article.urlToImage || 'http://localhost:3000/logo512.png',
          datePublished: article.publishedAt,
          dateModified: article.publishedAt,
          author: {
            '@type': 'Organization',
            name: article.source || 'Pharmaceutical Database'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Pharmaceutical Database',
            logo: {
              '@type': 'ImageObject',
              url: 'http://localhost:3000/logo512.png'
            }
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `http://localhost:3000/news/${id}`
          }
        }}
      />
      
      <button onClick={() => navigate(-1)} className="back-button">
        ← Back
      </button>

      <article className="news-detail-article">
        {/* Header Card with Gradient */}
        <div className="news-detail-header">
          <div className="news-category-badge">
            {article.category || 'Health & Pharma'}
          </div>
          <h1 className="news-detail-title">{article.title}</h1>
          
          {/* Drug Name Display */}
          {article.drugName && (
            <div className="news-drug-name">
              <span className="drug-name-icon">💊</span>
              <span className="drug-name-text">{article.drugName}</span>
            </div>
          )}
          
          <div className="news-detail-meta">
            <span className="meta-item">
              <span className="meta-icon">📰</span>
              <span className="meta-label">Source:</span>
              <span className="meta-value">{article.source}</span>
            </span>
            <span className="meta-item">
              <span className="meta-icon">📅</span>
              <span className="meta-label">Published:</span>
              <span className="meta-value">{formatDate(article.publishedAt)}</span>
            </span>
            {article.author && (
              <span className="meta-item">
                <span className="meta-icon">✍️</span>
                <span className="meta-label">Author:</span>
                <span className="meta-value">{article.author}</span>
              </span>
            )}
          </div>
        </div>

        {/* Featured Image */}
        {article.urlToImage && (
          <div className="news-detail-image-container">
            <img
              src={article.urlToImage}
              alt={article.title}
              className="news-detail-image"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Content Section */}
        <div className="news-detail-content">
          {article.description && (
            <div className="news-detail-description">
              <p className="description-text">{article.description}</p>
            </div>
          )}

          {article.content && (
            <div className="news-detail-body">
              <h3>Article Content</h3>
              <p className="body-text">
                {article.content.replace(/\[\+\d+ chars\]$/, '')}
              </p>
            </div>
          )}

          {/* Source Link Card */}
          <div className="news-detail-source-card">
            <h3>Read Full Article</h3>
            <p>For the complete article and additional information, visit the original source:</p>
            <a
              href={article.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="source-link-button"
            >
              <span className="source-link-icon">🔗</span>
              <span className="source-link-text">Visit {article.source}</span>
              <span className="source-link-arrow">→</span>
            </a>
            <p className="source-disclaimer">
              <small>Opens in a new tab. We are not responsible for external content.</small>
            </p>
          </div>

          {/* Additional Information */}
          <div className="news-detail-info-section">
            <h3>Article Information</h3>
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="info-label">Source Name</td>
                  <td className="info-value">{article.source}</td>
                </tr>
                <tr>
                  <td className="info-label">Publication Date</td>
                  <td className="info-value">{formatDate(article.publishedAt)}</td>
                </tr>
                {article.author && (
                  <tr>
                    <td className="info-label">Author</td>
                    <td className="info-value">{article.author}</td>
                  </tr>
                )}
                <tr>
                  <td className="info-label">Category</td>
                  <td className="info-value">{article.category || 'Health & Pharmaceutical'}</td>
                </tr>
                {article.readTime && (
                  <tr>
                    <td className="info-label">Reading Time</td>
                    <td className="info-value">{article.readTime}</td>
                  </tr>
                )}
                {article.views && (
                  <tr>
                    <td className="info-label">Views</td>
                    <td className="info-value">{article.views.toLocaleString()}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Study/Clinical Details */}
          {(article.trialSize || article.studySize || article.efficacyRate || article.studyDuration) && (
            <div className="news-detail-info-section">
              <h3>Study Details</h3>
              <table className="info-table">
                <tbody>
                  {(article.trialSize || article.studySize) && (
                    <tr>
                      <td className="info-label">Study Size</td>
                      <td className="info-value">{article.trialSize || article.studySize}</td>
                    </tr>
                  )}
                  {article.studyDuration && (
                    <tr>
                      <td className="info-label">Duration</td>
                      <td className="info-value">{article.studyDuration}</td>
                    </tr>
                  )}
                  {article.efficacyRate && (
                    <tr>
                      <td className="info-label">Efficacy</td>
                      <td className="info-value">{article.efficacyRate}</td>
                    </tr>
                  )}
                  {article.followUpPeriod && (
                    <tr>
                      <td className="info-label">Follow-up Period</td>
                      <td className="info-value">{article.followUpPeriod}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Medical/Clinical Information */}
          {(article.drugName || article.clinicalTrialPhase || article.fdaStatus || article.mechanismOfAction || article.affectedPopulation) && (
            <div className="news-detail-info-section">
              <h3>Clinical Information</h3>
              <table className="info-table">
                <tbody>
                  {article.drugName && (
                    <tr>
                      <td className="info-label">Drug Name</td>
                      <td className="info-value" style={{ fontWeight: '700', color: '#667eea' }}>{article.drugName}</td>
                    </tr>
                  )}
                  {article.clinicalTrialPhase && (
                    <tr>
                      <td className="info-label">Clinical Trial Phase</td>
                      <td className="info-value">{article.clinicalTrialPhase}</td>
                    </tr>
                  )}
                  {article.fdaStatus && (
                    <tr>
                      <td className="info-label">FDA Status</td>
                      <td className="info-value">{article.fdaStatus}</td>
                    </tr>
                  )}
                  {article.mechanismOfAction && (
                    <tr>
                      <td className="info-label">Mechanism of Action</td>
                      <td className="info-value">{article.mechanismOfAction}</td>
                    </tr>
                  )}
                  {article.affectedPopulation && (
                    <tr>
                      <td className="info-label">Affected Population</td>
                      <td className="info-value">{article.affectedPopulation}</td>
                    </tr>
                  )}
                  {article.approvalDate && (
                    <tr>
                      <td className="info-label">Approval Date</td>
                      <td className="info-value">{formatDate(article.approvalDate)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Tags */}
          {article.tags && article.tags.length > 0 && (
            <div className="news-detail-tags">
              <h3>Related Topics</h3>
              <div className="tags-container">
                {article.tags.map((tag, index) => (
                  <span key={index} className="tag-badge">{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}

export default NewsDetail;
